/**
 * Service para Recepciones Visuales - Frontend
 */

import api from './api.js';

class RecepcionService {
  /**
   * Listar recepciones con filtros
   */
  async listar(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.estado_general) queryParams.append('estado_general', params.estado_general);
    if (params.marca) queryParams.append('marca', params.marca);
    if (params.fecha_desde) queryParams.append('fecha_desde', params.fecha_desde);
    if (params.fecha_hasta) queryParams.append('fecha_hasta', params.fecha_hasta);
    if (params.busqueda) queryParams.append('busqueda', params.busqueda);
    if (params.tipo_recepcion) queryParams.append('tipo_recepcion', params.tipo_recepcion);

    const response = await api.get(`/almacen/recepciones?${queryParams.toString()}`);
    // El backend usa successWithPagination: la paginación viaja al nivel raíz
    // (response.pagination), no dentro de response.data. Reagrupamos para que
    // el caller reciba { recepciones, pagination } como espera.
    return {
      recepciones: response.data,
      pagination: response.pagination
    };
  }

  /**
   * Obtener recepción por ID
   */
  async obtenerPorId(id) {
    const response = await api.get(`/almacen/recepciones/${id}`);
    return response.data; // Devuelve la recepción
  }

  /**
   * Obtener recepción por número de serie
   */
  async obtenerPorNumSerie(numSerie) {
    const response = await api.get(`/almacen/recepciones/num-serie/${numSerie}`);
    return response.data; // Devuelve la recepción
  }

  /**
   * Crear nueva recepción
   */
  async crear(data) {
    const response = await api.post('/almacen/recepciones', data);
    return response.data; // Devuelve la recepción creada
  }

  /**
   * Actualizar recepción
   */
  async actualizar(id, data) {
    const response = await api.put(`/almacen/recepciones/${id}`, data);
    return response.data; // Devuelve la recepción actualizada
  }

  /**
   * Completar recepción (borrador → completada)
   */
  async completar(id) {
    const response = await api.patch(`/almacen/recepciones/${id}/completar`);
    return response.data;
  }

  /**
   * Subir/reemplazar SOLO el video de inspección.
   * Usa el permiso dedicado `recepciones:subir_video` (independiente de editar).
   */
  async actualizarVideo(id, videoUrl) {
    const response = await api.patch(`/almacen/recepciones/${id}/video`, { video_url: videoUrl });
    return response.data;
  }

  /**
   * Eliminar recepción
   */
  async eliminar(id) {
    const response = await api.delete(`/almacen/recepciones/${id}`);
    return response.data; // Devuelve null
  }

  /**
   * Obtener estadísticas
   */
  async obtenerEstadisticas() {
    const response = await api.get('/almacen/recepciones/stats');
    return response.data; // Devuelve las estadísticas
  }

  /**
   * Listar pedidos con progreso de recepción (Flujo A - Por OC)
   */
  async listarPedidos(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.soloPendientes !== undefined) queryParams.append('soloPendientes', params.soloPendientes);
    if (params.busqueda) queryParams.append('busqueda', params.busqueda);
    if (params.fecha_desde) queryParams.append('fecha_desde', params.fecha_desde);
    if (params.fecha_hasta) queryParams.append('fecha_hasta', params.fecha_hasta);
    if (params.proveedor_id) queryParams.append('proveedor_id', params.proveedor_id);

    const response = await api.get(`/almacen/recepciones/pedidos?${queryParams.toString()}`);
    return response.data; // { pedidos, pagination } o array directo según response.util
  }

  /**
   * Detalle de un pedido para Recepción (unidades + progreso)
   */
  async obtenerPedidoParaRecepcion(id) {
    const response = await api.get(`/almacen/recepciones/pedidos/${id}`);
    return response.data;
  }

  /**
   * Asignar número de serie a una unidad (crea tractor)
   */
  async asignarSerieAUnidad(unidadId, data) {
    const response = await api.post(`/almacen/recepciones/unidades/${unidadId}/asignar-serie`, data);
    return response.data;
  }

  /**
   * Corregir el num_serie de una unidad (typo).
   * Renombra tractor con cascada. Gates: unidad en 'recibido', recepción
   * no completada y sin firmas.
   */
  async corregirSerieUnidad(unidadId, numSerieNuevo) {
    const response = await api.put(
      `/almacen/recepciones/unidades/${unidadId}/corregir-serie`,
      { num_serie_nuevo: numSerieNuevo }
    );
    return response.data;
  }

  /**
   * Liberar el num_serie de una unidad (deshacer asignación al slot equivocado).
   * Hard delete del tractor + soft delete de recepción borrador si existe.
   * Gates: unidad en 'recibido', recepción no completada y sin firmas, tractor sin otras dependencias.
   */
  async liberarSerieUnidad(unidadId) {
    const response = await api.delete(
      `/almacen/recepciones/unidades/${unidadId}/liberar-serie`
    );
    return response.data;
  }

  /**
   * Diagnóstico de un NS (determina tipo de recepción)
   */
  async lookupNumSerie(numSerie) {
    const response = await api.get(`/almacen/recepciones/ns-lookup/${encodeURIComponent(numSerie)}`);
    return response.data;
  }

  /**
   * Subir firma digital
   * @param {Blob} blob - Archivo de firma
   * @param {string} nombreArchivo - Nombre del archivo
   * @param {string} [numSerie] - Si se pasa, sube al bucket unificado
   * @param {string} [modulo='recepciones'] - Módulo destino (recepciones, pnc, etc.)
   * Ruta destino: `{modulo}/{numSerie}/firmas/`
   */
  async subirFirma(blob, nombreArchivo, numSerie, modulo = 'recepciones') {
    const formData = new FormData();
    formData.append('firma', blob, nombreArchivo);

    if (numSerie) {
      formData.append('modulo', modulo);
      formData.append('num_serie', numSerie);
      formData.append('tipo', 'firmas');
    }

    const response = await api.upload('/files/firma', formData);
    return response.data.url;
  }

  /**
   * Subir foto
   * @param {File|Blob} file - Archivo a subir
   * @param {string} nombreArchivo - Nombre del archivo
   * @param {string} [numSerie] - Si se pasa, sube al bucket unificado
   * @param {string} [modulo='recepciones'] - Módulo destino (recepciones, pnc, etc.)
   * Ruta destino: `{modulo}/{numSerie}/fotos/`
   */
  async subirFoto(file, nombreArchivo, numSerie, modulo = 'recepciones') {
    const formData = new FormData();
    formData.append('foto', file, nombreArchivo);

    if (numSerie) {
      formData.append('modulo', modulo);
      formData.append('num_serie', numSerie);
      formData.append('tipo', 'fotos');
    }

    const response = await api.upload('/files/foto', formData);
    return response.data.url;
  }

  /**
   * Subir múltiples fotos
   * @param {Array} fotos - Archivos a subir
   * @param {string} prefijo - Prefijo para los nombres (típicamente el num_serie)
   * @param {Function} onProgress - Callback de progreso
   */
  async subirMultiplesFotos(fotos, prefijo, onProgress, modulo = 'recepciones') {
    const urls = [];
    const errores = [];
    const total = fotos.length;

    // El prefijo es el num_serie. Lo usamos para subir al bucket unificado.
    const numSerie = prefijo;

    for (let i = 0; i < total; i++) {
      try {
        const nombre = `${prefijo}_${i + 1}.jpg`;
        const url = await this.subirFoto(fotos[i], nombre, numSerie, modulo);
        urls.push(url);
      } catch (error) {
        console.error(`Error subiendo foto ${i + 1}:`, error);
        errores.push(i + 1);
      }

      if (onProgress) {
        onProgress({
          current: i + 1,
          total,
          percentage: Math.round(((i + 1) / total) * 100)
        });
      }
    }

    if (errores.length > 0) {
      toast.warning(`${errores.length} de ${total} fotos no se pudieron subir (foto${errores.length > 1 ? 's' : ''} ${errores.join(', ')})`);
    }

    return urls;
  }

  /**
   * Subir video
   * @param {File|Blob} file - Archivo a subir
   * @param {string} nombreArchivo - Nombre del archivo
   * @param {string} [numSerie] - Si se pasa, sube al bucket unificado (agromx-storage/recepciones/{numSerie}/videos/)
   */
  async subirVideo(file, nombreArchivo, numSerie, modulo = 'recepciones') {
    const formData = new FormData();
    formData.append('video', file, nombreArchivo);

    if (numSerie) {
      formData.append('modulo', modulo);
      formData.append('num_serie', numSerie);
      formData.append('tipo', 'videos');
    }

    const response = await api.upload('/files/video', formData);
    return response.data.url;
  }

  /**
   * Subir documento PDF
   */
  async subirDocumento(file, nombreArchivo) {
    const formData = new FormData();
    formData.append('documento', file, nombreArchivo);

    const response = await api.upload('/files/documento', formData);
    return response.data.url;
  }

  async eliminarArchivo(fileUrl) {
    return api.post('/files/eliminar', { url: fileUrl });
  }
}

export default new RecepcionService();
